import { css } from '@emotion/core'
import { Cell, Grid, H5 } from '@fs/zion-ui'
import React, { useContext, useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../hooks/useNotifications'
import { deleteFact, postFactConclusion, putFactConclusion } from '../../../services/personService'
import { getLastPathElement } from '../../../util/StringUtil'
import { deepCopy } from '../../../util/Utils'
import { DmcConclusionFact } from '../../common/conclusionFact/DmcConclusionFact'
import { DmcNewConclusionFact } from '../../common/conclusionFact/DmcNewConclusionFact'
import { PersonContext } from '../PersonContext'
import FactDataView from './FactDataView'
import PSAttribution from './PSAttribution'

// noinspection JSUnusedGlobalSymbols
export default () => {}

const paddingBtm = css`
  padding-bottom: 15px;
`

export function FactsSummary({ editable }) {
  const { t } = useTranslation()
  const { personFactsSummaryState, personFactsSummaryActions } = usePersonFactsSummary()
  const newFactOptions = [
    {
      label: t('birth', 'Birth'),
      value: 'http://gedcomx.org/Birth',
    },
    {
      label: t('burial', 'Burial'),
      value: 'http://gedcomx.org/Burial',
    },
    {
      label: t('christening', 'Christening'),
      value: 'http://gedcomx.org/Christening',
    },
    {
      label: t('death', 'Death'),
      value: 'http://gedcomx.org/Death',
    },
  ]
  return (
    <>
      <div css={paddingBtm}>
        <H5 supplementary>{t('facts', 'Facts')}</H5>
      </div>
      <Grid>
        {personFactsSummaryState.facts &&
          personFactsSummaryState.facts.map((fact) => {
            const noDelete =
              getLastPathElement(fact.value.type) === 'Death' &&
              personFactsSummaryState.facts.filter((f) => getLastPathElement(f.value.type) === 'Death').length === 1
            return editable ? (
              <React.Fragment key={fact.conclusionId}>
                {/* Row */}
                <Cell columns={12}>
                  <DmcConclusionFact
                    editable={editable}
                    noDelete={noDelete}
                    factConclusion={fact}
                    factOptions={[
                      {
                        label: t(
                          getLastPathElement(fact.value.type).toLowerCase(),
                          getLastPathElement(fact.value.type)
                        ),
                        value: fact.value.type,
                      },
                    ]}
                    onSaveCallback={(factConclusion, changeMessage) =>
                      personFactsSummaryActions.updateFact(factConclusion, fact, changeMessage)
                    }
                    onDeleteCallback={(factConclusion, changeMessage) =>
                      personFactsSummaryActions.deleteFact(factConclusion, changeMessage)
                    }
                  />
                </Cell>

                {/* Row */}
                <PSAttribution attribution={fact.attribution} cellColumns={10} />
              </React.Fragment>
            ) : (
              <React.Fragment key={fact.conclusionId}>
                <Cell columns={12}>
                  <FactDataView fact={fact} />
                </Cell>
              </React.Fragment>
            )
          })}
        {/* Row */}
        <Cell columns={12}>
          <DmcNewConclusionFact
            factOptions={newFactOptions}
            onCreateCallback={(newConclusionFact) => personFactsSummaryActions.createFact(newConclusionFact)}
          />
        </Cell>
      </Grid>
    </>
  )
}

function usePersonFactsSummary() {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const { currentPerson, fetchCurrentPerson } = useContext(PersonContext)

  const initialState = {
    facts: currentPerson.factConclusions ? sortFacts(currentPerson.factConclusions) : currentPerson.factConclusions,
  }
  const reducer = (_, currentState) => deepCopy(currentState)
  const [personFactsSummaryState, setPersonFactsSummaryState] = useReducer(reducer, initialState)

  function sortFacts(facts) {
    const isPriorityFactType = (type) => {
      return type === 'Birth' || type === 'Burial' || type === 'Christening' || type === 'Death'
    }

    if (!facts) return null

    const priorityFacts = facts.filter((fact) => isPriorityFactType(getLastPathElement(fact.value.type)))
    const nonPriorityFacts = facts.filter((fact) => !isPriorityFactType(getLastPathElement(fact.value.type)))

    return priorityFacts.concat(nonPriorityFacts)
  }

  const personFactsSummaryActions = {
    setFacts: (facts) => {
      personFactsSummaryState.facts = sortFacts(facts)
    },
    updateFact: (modifiedFact, originalFact, changeMessage) => {
      modifiedFact.conclusionId = originalFact.conclusionId
      if (originalFact.value.userDefinedDataType) {
        modifiedFact.value.userDefinedDataType = originalFact.value.userDefinedDataType
        modifiedFact.value.userDefinedType = originalFact.value.userDefinedType
      }
      putFactConclusion(currentPerson.id, modifiedFact, changeMessage)
        .then(() => {
          fetchCurrentPerson()
        })
        .catch((err) => {
          error(
            t('person.summary.errors.editingPersonFact', {
              defaultValue: `Error editing person fact: ${err.message || ''}`,
              message: err.message || '',
            }),
            err
          )
        })
    },
    deleteFact: (conclusionFact, changeMessage) => {
      deleteFact(currentPerson.id, conclusionFact.conclusionId, changeMessage)
        .then(() => {
          fetchCurrentPerson()
        })
        .catch((err) => {
          error(
            t('person.summary.errors.deletingPersonFact', {
              defaultValue: `Error deleting a person fact: ${err.message || ''}`,
              message: err.message || '',
            }),
            err
          )
        })
    },
    createFact: (conclusionFact) => {
      postFactConclusion(currentPerson.id, conclusionFact)
        .then(() => {
          fetchCurrentPerson()
        })
        .catch((err) => {
          error(
            t('person.summary.errors.creatingPersonFact', {
              defaultValue: `Error creating a person fact: ${err.message || ''}`,
              message: err.message || '',
            }),
            err
          )
        })
    },
    updatePersonFactsSummaryState: () => {
      setPersonFactsSummaryState(personFactsSummaryState)
    },
  }

  personFactsSummaryActions.setFacts(
    currentPerson.factConclusions ? sortFacts(currentPerson.factConclusions) : currentPerson.factConclusions
  )

  return { personFactsSummaryState, personFactsSummaryActions }
}
